// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { JobTitle, UserEnrollmentResponse } from '@interfaces/user.interfaces';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';
import { RpcApiResponse } from '@interfaces/api.interfaces';
import { ProductIds } from '@interfaces/document.interfaces';
import { SsoUserLookupResponse } from '@interfaces/auth.interfaces';

@Injectable({ providedIn: 'root' })
export class EnrollmentService {
    constructor(private readonly apiService: ApiService) {}

    enroll(
        token: string | null,
        jacketId: string | null,
        oemDealerId: string,
        email: string,
        firstName: string,
        lastName: string,
        phone: string,
        jobTitle: string
    ): Observable<UserEnrollmentResponse> {
        return this.apiService
            .postUnauthenticatedV2<UserEnrollmentResponse>('api/auth/enrollment/enroll', {
                token,
                jacketId,
                oemDealerId,
                email,
                lastName,
                firstName,
                phone,
                jobTitle,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response: RpcApiResponse<UserEnrollmentResponse>) => {
                    return response?.data;
                })
            );
    }

    getEnrollmentInfoFromJacketId$(jacketId: number): Observable<{ isActive: boolean; oemCode?: string }> {
        return this.apiService
            .getV2$<{ productId?: number; active: boolean; oemCode?: string }[]>(`api/dealer/admin/enrollment/jacket/${jacketId}`, null, false)
            .pipe(
                map((enrollments) => {
                    // there are many enrollments, like fish in the sea...
                    for (const enrollment of enrollments.data) {
                        if (ProductIds.EzDocsBase === enrollment.productId) {
                            // ...but only one ever really matters
                            return { isActive: enrollment.active, oemCode: enrollment.oemCode };
                        }
                    }

                    return null;
                })
            );
    }

    getJobTitles(token: string | null, jacketId: string | null, oemDealerId: string): Observable<JobTitle[]> {
        return this.apiService
            .getV2$<JobTitle[]>(`api/auth/enrollment/jobTitles?token=${token ?? ''}&jacketId=${jacketId ?? ''}&oemDealerId=${oemDealerId}`, null, false)
            .pipe(
                map((response) => {
                    return response?.data;
                })
            );
    }

    getUserByEmail$(token: string, jacketId: string, oemDealerId: string, email: string): Observable<SsoUserLookupResponse> {
        return this.apiService.postUnauthenticatedV2<SsoUserLookupResponse>('api/auth/enrollment/user?', { token, jacketId, oemDealerId, email }).pipe(
            map((response) => {
                return response?.data;
            })
        );
    }

    sendEnrollmentNotification(dealerId, firstName, lastName, email, phone): Observable<boolean> {
        return this.apiService
            .postV2$<boolean>('api/auth/enrollment/enrollmentConfirmation', {
                dealerId,
                firstName,
                lastName,
                email,
                phone,
            })
            .pipe(map((response) => response?.data ?? false));
    }

    setUserPassword(token: string | null, jacketId: string | null, oemDealerId: string, logonId: string, newPassword: string): Observable<boolean> {
        return this.apiService
            .postUnauthenticatedV2<boolean>('api/auth/enrollment/setUserPassword', {
                token,
                jacketId,
                oemDealerId,
                logonId,
                newPassword,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response) => {
                    if (response && response.data) {
                        return response.data;
                    }
                })
            );
    }

    requestAccess(token: string, firstName: string, lastName: string, email: string, phone: string): Observable<boolean> {
        return this.apiService
            .postUnauthenticatedV2<boolean>('api/auth/enrollment/requestAccess', {
                token,
                firstName,
                lastName,
                email,
                phone,
            })
            .pipe(
                catchError((error) => {
                    return of(error);
                }),
                map((response) => {
                    if (response && response.data) {
                        return response.data;
                    }
                })
            );
    }

    requestAccessByJacketId(jacketId: string, firstName: string, lastName: string, email: string, phone: string): Observable<boolean> {
        return this.apiService
            .postUnauthenticatedV2<boolean>('api/auth/enrollment/requestAccessByJacketId', {
                jacketId,
                firstName,
                lastName,
                email,
                phone,
            })
            .pipe(
                map((response) => {
                    return response.data;
                })
            );
    }
}
